import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore, useStoreState } from '@proscom/prostore-react';
import { LocationStore } from '@proscom/prostore-react-router';
import { startOfToday } from 'date-fns';
import {
  TransformedQueryParams,
  URL_KEY_BIRTHDAY_END,
  URL_KEY_BIRTHDAY_START,
  URL_KEY_DIRECTOR_ID,
  URL_KEY_FILTER_SKILL_IDS,
  URL_KEY_IS_DIRECTOR,
  URL_KEY_VACATION_TYPE,
  URL_KEY_VACATION_SUBTYPE,
  URL_KEY_DISTANT_TYPE,
  URL_KEY_EVENT,
  URL_KEY_PAGE,
  URL_KEY_SEARCH,
  URL_KEY_UNIT_ID,
  URL_KEY_ABSENCE_DATES_START,
  URL_KEY_ABSENCE_DATES_END,
  URL_KEY_IS_INCLUDE_SUBORDINATE_UNITS
} from '../../../store/urlKeys';

import { formatDate } from '../../../common/hooks/useFormatDate';
import { STORE_LOCATION } from '../../../store/storeKeys';
import { FilterSkills } from '../../../common/components/utils/filters/FilterSkills';
import { FilterSearch } from '../../../common/components/ui/Filter/FilterSearch';
import { UserFilter } from '../../../graphql/types';
import {
  FilterAbsence,
  getVacationTypesAndSubtypes
} from '../../../common/components/utils/filters/FilterAbsence';
import { FilterDirector } from '../../../common/components/utils/filters/FilterDirector';
import { FilterUnit } from '../../../common/components/utils/filters/FilterUnit';
import { valueToArray } from '../../../utils/objects';
import { Checkbox } from '../../../common/components/ui/Checkbox/Checkbox';
import { FilterEvent } from '../../../common/components/utils/filters/FilterEvent';
import { Event } from '../../../common/components/utils/filters/FilterEvent';
import { AbsenceTypesStoreState } from '../../../store/AbsenceTypesStore';
import s from './UsersPage.module.scss';

export const useUsersFilter = ({
  showSearchInput = true
}: {
  showSearchInput?: boolean;
}) => {
  const [locationState, locationStore] = useStore<LocationStore>(
    STORE_LOCATION
  );
  const query = (locationState.query || {}) as TransformedQueryParams;
  const changeQuery = locationStore.changeQuery;

  const [time, setTime] = useState(0);

  const convertTypes = (types, initialArray, allTypes) => {
    let result: string[] = [];

    if (!types || !types.length) {
      return result;
    }

    types.forEach((type) => {
      const typeName = initialArray[type];

      const selectedTypes = allTypes.filter(
        (allType) =>
          allType.category === typeName &&
          allType.checkbox === 'Не детализируется'
      );
      if (selectedTypes && selectedTypes.length) {
        result.push(
          ...selectedTypes.map((selectedType) => selectedType.type.toString())
        );
      }
    });

    return result;
  };

  const convertSubTypes = (types, initialArray, allTypes) => {
    let result: string[] = [];

    if (!types || !types.length) {
      return result;
    }

    types.forEach((type) => {
      const typeName = initialArray[type];

      const selectedTypes = allTypes.filter(
        (allType) => allType.checkbox === typeName
      );
      if (selectedTypes && selectedTypes.length) {
        result.push(
          ...selectedTypes.map((selectedType) => selectedType.type.toString())
        );
      }
    });

    return result;
  };

  const { absenceTypes } = useStoreState<AbsenceTypesStoreState>(
    'STORE_ABSENCE_TYPES'
  );

  const types = useMemo(() => {
    return getVacationTypesAndSubtypes(absenceTypes);
  }, [absenceTypes]);

  const vacationTypes = types.types;
  const vacationSubtypes = types.subtypes;

  const filter: UserFilter = {
    search: query[URL_KEY_SEARCH],
    unit_ids: query[URL_KEY_UNIT_ID],
    is_include_subordinate_units: Boolean(
      query[URL_KEY_IS_INCLUDE_SUBORDINATE_UNITS]
    ),
    director_ids: query[URL_KEY_DIRECTOR_ID],
    skill_ids: query[URL_KEY_FILTER_SKILL_IDS],
    is_director: Boolean(query[URL_KEY_IS_DIRECTOR]),
    ...(Boolean(
      query[URL_KEY_BIRTHDAY_START] && query[URL_KEY_BIRTHDAY_END]
    ) && {
      birthday: {
        start: query[URL_KEY_BIRTHDAY_START],
        end: query[URL_KEY_BIRTHDAY_END]
      }
    }),
    absence_dates: {
      start: query[URL_KEY_ABSENCE_DATES_START],
      end: query[URL_KEY_ABSENCE_DATES_END]
    },
    vacation_type: query[URL_KEY_VACATION_TYPE]
      ? convertTypes(
          query[URL_KEY_VACATION_TYPE].split(','),
          vacationTypes,
          absenceTypes
        )
      : [],
    vacation_subtype: query[URL_KEY_VACATION_SUBTYPE]
      ? convertSubTypes(
          query[URL_KEY_VACATION_SUBTYPE].split(','),
          vacationSubtypes,
          absenceTypes
        )
      : [],
    absence_type: query[URL_KEY_EVENT],
    dist_work_types: query[URL_KEY_DISTANT_TYPE]
      ? query[URL_KEY_DISTANT_TYPE].split(',')
      : [],
    time: time
  };

  const handleFilterChange = useCallback(
    (key: string, valueConverter?: (value) => any) => {
      return (value) => {
        const val = valueConverter ? valueConverter(value) : value;

        changeQuery({
          [key]: val || undefined,
          [URL_KEY_PAGE]: 1
        });
      };
    },
    [changeQuery]
  );

  // сработает один раз, чтобы по умолчанию проставить галку в чекбокс
  useEffect(() => {
    handleFilterChange(URL_KEY_IS_INCLUDE_SUBORDINATE_UNITS)(true);
  }, [handleFilterChange]);

  const todayDate = formatDate(startOfToday(), 'yyyy-MM-dd');

  // при выборе события "отсутствия" проставляем все чекбоксы
  const onSelectEventVacations = () => {
    changeQuery({
      [URL_KEY_EVENT]: Event.vacations,
      [URL_KEY_VACATION_TYPE]: Object.keys(vacationTypes).join(','),
      [URL_KEY_VACATION_SUBTYPE]: Object.keys(vacationSubtypes).join(','),
      [URL_KEY_PAGE]: 1,
      [URL_KEY_ABSENCE_DATES_START]: todayDate,
      [URL_KEY_ABSENCE_DATES_END]: todayDate
    });
  };

  // тоже самое для дистанционной работы
  const onSelectEventDistWork = () => {
    changeQuery({
      [URL_KEY_EVENT]: Event.dist_work,
      [URL_KEY_DISTANT_TYPE]: '1,2',
      [URL_KEY_ABSENCE_DATES_START]: todayDate,
      [URL_KEY_ABSENCE_DATES_END]: todayDate
    });
  };

  const onAbsenceFilterChange = () => {
    setTime(time + 1);
  };

  const filterSearchComponent: React.ReactNode = (
    <FilterSearch
      className={s.UsersFilter__filterSearch}
      classes={{
        filter: {
          content: s.UsersFilter__filterSearchContent
        }
      }}
      value={filter.search || ''}
      onChange={handleFilterChange(URL_KEY_SEARCH)}
    />
  );

  const filterComponent: React.ReactNode = (
    <div className={s.UsersFilter}>
      {showSearchInput && filterSearchComponent}
      <div className={s.UsersFilter__filters}>
        <FilterUnit
          className={s.UsersFilter__filter}
          label={'Отдел'}
          value={filter.unit_ids?.[0]}
          onChange={handleFilterChange(URL_KEY_UNIT_ID, valueToArray)}
          isClearable
        />

        <div className={s.UsersFilter__filter}>
          <Checkbox
            checked={Boolean(filter.is_include_subordinate_units)}
            onChange={handleFilterChange(URL_KEY_IS_INCLUDE_SUBORDINATE_UNITS)}
            label={'Подчиненные подразделения'}
          />
        </div>

        <FilterDirector
          className={s.UsersFilter__filter}
          label={'Руководитель'}
          value={filter.director_ids?.[0]}
          onChange={handleFilterChange(URL_KEY_DIRECTOR_ID, valueToArray)}
          isClearable
        />

        <FilterSkills
          className={s.UsersFilter__filter}
          label={'Компетенции'}
          value={filter.skill_ids}
          onChange={handleFilterChange(URL_KEY_FILTER_SKILL_IDS)}
        />

        <FilterEvent
          className={s.UsersFilter__filter}
          datePickerClasses={{
            input: s.UsersFilter__datePickerInput,
            label: s.UsersFilter__datePickerLabel
          }}
          query={query}
          filter={filter}
          changeQuery={changeQuery}
          handleFilterChange={handleFilterChange}
          onSelectEventVacations={onSelectEventVacations}
          onSelectEventDistWork={onSelectEventDistWork}
        />

        <div className={s.UsersFilter__filter}>
          <Checkbox
            checked={Boolean(filter.is_director)}
            onChange={handleFilterChange(URL_KEY_IS_DIRECTOR)}
            label={'Только Руководители'}
          />
        </div>

        <FilterAbsence
          className={s.UsersFilter__filter}
          query={query}
          filter={filter}
          changeQuery={changeQuery}
          handleFilterChange={handleFilterChange}
          onFilterChange={onAbsenceFilterChange}
        />
      </div>
    </div>
  );

  return [filterComponent, filter, filterSearchComponent] as const;
};
